import { LatLng, UniversalMapEntry } from '../MapInterface';
import { MapBounds } from '../StaticMapInterface';

/**
 * Static map utility functions
 */

/**
 * Calculate the center point from an array of entries
 * @param entries - Array of map entries
 * @returns Center point coordinates
 */
export const calculateCenter = (entries: UniversalMapEntry[]): LatLng => {
  if (entries.length === 0) {
    // Default to center of United States
    return { lat: 39.8283, lng: -98.5795 };
  }

  if (entries.length === 1) {
    return { lat: entries[0].location.lat, lng: entries[0].location.lng };
  }

  const sum = entries.reduce(
    (acc, entry) => ({
      lat: acc.lat + entry.location.lat,
      lng: acc.lng + entry.location.lng,
    }),
    { lat: 0, lng: 0 }
  );

  return {
    lat: sum.lat / entries.length,
    lng: sum.lng / entries.length,
  };
};

/**
 * Calculate bounds from an array of entries
 * @param entries - Array of map entries
 * @returns Bounds object
 */
export const calculateBounds = (entries: UniversalMapEntry[]): MapBounds => {
  if (entries.length === 0) {
    // Default bounds for US
    return {
      north: 49.3457868,
      south: 24.7433195,
      east: -66.9513812,
      west: -124.7844079,
    };
  }

  const bounds = entries.reduce(
    (acc, entry) => ({
      north: Math.max(acc.north, entry.location.lat),
      south: Math.min(acc.south, entry.location.lat),
      east: Math.max(acc.east, entry.location.lng),
      west: Math.min(acc.west, entry.location.lng),
    }),
    {
      north: entries[0].location.lat,
      south: entries[0].location.lat,
      east: entries[0].location.lng,
      west: entries[0].location.lng,
    }
  );

  return bounds;
};

/**
 * Calculate optimal zoom level based on entries
 * @param entries - Array of map entries
 * @param mapWidth - Width of the map in pixels
 * @param mapHeight - Height of the map in pixels
 * @returns Optimal zoom level (1-20)
 */
export const calculateOptimalZoom = (
  entries: UniversalMapEntry[],
  mapWidth: number,
  mapHeight: number
): number => {
  if (entries.length <= 1) {
    return 10; // Default zoom for single entry or no entries
  }

  const bounds = calculateBounds(entries);
  
  // Calculate the span of the bounds
  const latSpan = bounds.north - bounds.south;
  const lngSpan = bounds.east - bounds.west;
  
  // Add padding (20% on each side)
  const paddedLatSpan = latSpan * 1.4;
  const paddedLngSpan = lngSpan * 1.4;
  
  // Calculate zoom based on the larger span
  const maxSpan = Math.max(paddedLatSpan, paddedLngSpan);
  
  // Rough conversion from degrees to zoom level
  // This is an approximation and varies by latitude
  let zoom = Math.floor(Math.log2(360 / maxSpan));
  
  // Clamp between reasonable values
  zoom = Math.max(1, Math.min(18, zoom));
  
  return zoom;
};

/**
 * Validate latitude and longitude values
 * @param lat - Latitude value
 * @param lng - Longitude value
 * @returns True if valid coordinates
 */
export const validateCoordinates = (lat: number, lng: number): boolean => {
  return (
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180 &&
    !isNaN(lat) &&
    !isNaN(lng)
  );
};

/**
 * Sanitize entries by removing invalid coordinates
 * @param entries - Array of map entries
 * @returns Filtered array with valid entries only
 */
export const sanitizeEntries = (entries: UniversalMapEntry[]): UniversalMapEntry[] => {
  return entries.filter(entry => 
    validateCoordinates(entry.location.lat, entry.location.lng)
  );
};

/**
 * Format coordinates for URL usage
 * @param lat - Latitude
 * @param lng - Longitude
 * @param precision - Number of decimal places (default: 6)
 * @returns Formatted coordinate string
 */
export const formatCoordinates = (lat: number, lng: number, precision: number = 6): string => {
  return `${lat.toFixed(precision)},${lng.toFixed(precision)}`;
};

/**
 * Check if URL is approaching length limits
 * @param url - URL string to check
 * @param provider - Map provider
 * @returns Warning object if URL is too long
 */
export const checkUrlLength = (url: string, provider: 'google' | 'mapbox') => {
  const limits = {
    google: 8192, // Google's documented limit
    mapbox: 8192, // Conservative limit for Mapbox
  };

  const limit = limits[provider];
  const length = url.length;
  
  if (length > limit * 0.9) {
    return {
      warning: true,
      length,
      limit,
      message: `URL approaching ${provider} length limit (${length}/${limit} characters)`,
    };
  }

  return {
    warning: false,
    length,
    limit,
  };
};